/**
 * Builds a stable navigation tree from route config without mutating the source objects.
 */

export function buildNavigationMenu(routes = []) {
    let nextId = 1;

    const mapItem = (item, parentId = null) => {
        const id = nextId;
        nextId += 1;

        const mapped = {
            id,
            parentId,
            name: item.name,
            path: item.path,
            icon: item.icon,
            roles: item.roles,
            badge: item.badge,
        };

        if (Array.isArray(item.children)) {
            const children = item.children.filter((child) => child.name).map((child) => mapItem(child, id));
            if (children.length > 0) {
                mapped.children = children;
            }
        }

        return mapped;
    };

    return routes.filter((item) => item.name).map((item) => mapItem(item));
}

/**
 * Keeps the items the given role is allowed to see, at every level.
 *
 * Children usually inherit their section's access and carry no `roles` of their
 * own, but a child may narrow it (an admin-only screen inside a section support
 * can otherwise use). Such an item is dropped here so it never shows up in the
 * sidebar as a link that only redirects. A section whose children are all
 * filtered out is dropped with them.
 */
export function filterMenuByRole(menuItems = [], role) {
    const isAllowed = (item) => !item.roles || item.roles.length === 0 || item.roles.includes(role);

    return menuItems.reduce((allowedItems, item) => {
        if (!isAllowed(item)) {
            return allowedItems;
        }

        if (!Array.isArray(item.children)) {
            allowedItems.push(item);
            return allowedItems;
        }

        const children = filterMenuByRole(item.children, role);
        if (children.length > 0) {
            allowedItems.push({ ...item, children });
        }
        return allowedItems;
    }, []);
}

/**
 * Returns menu item ids that should appear active for the current pathname.
 * Matches exact path or pathname prefix; parents of matching children are included.
 */
export function getActivatedMenuItemIds(menuItems = [], pathname = '') {
    const normalize = (path) => (path && path !== '/' ? path.replace(/\/+$/, '') : path || '');
    const target = normalize(pathname);
    const matchingItems = [];

    for (const menuItem of menuItems) {
        const itemPath = normalize(menuItem.path);
        const selfMatches =
            itemPath && (target === itemPath || (itemPath !== '/' && target.startsWith(`${itemPath}/`)));

        const childMatches = Array.isArray(menuItem.children)
            ? getActivatedMenuItemIds(menuItem.children, target)
            : [];

        if (selfMatches) {
            matchingItems.push(menuItem.id);
        }

        if (childMatches.length > 0) {
            matchingItems.push(...childMatches, menuItem.id);
        }
    }

    return Array.from(new Set(matchingItems));
}

/**
 * Accordion expand: only one menu section open at a time.
 * Clicking the open item closes it; clicking another opens that one alone.
 */
export function toggleExpandedItem(expandedItems = [], itemId) {
    if (expandedItems.includes(itemId)) {
        return [];
    }
    return [itemId];
}
